// src/components/Settings.tsx

import { useState, useEffect } from 'react';
import type { Settings as SettingsType } from '../types';
import apiService from '../services/api';

function Settings() {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getSettings();
      setSettings(data);
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiService.updateSettings(settings);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateCacheSetting = (key: keyof SettingsType['cache'], value: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      cache: {
        ...settings.cache,
        [key]: value,
      },
    });
  };

  const updateTorSetting = (key: keyof SettingsType['torProject'], value: number | string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      torProject: {
        ...settings.torProject,
        [key]: value,
      },
    });
  };

  const updateApiSetting = (key: keyof SettingsType['externalApis'], value: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      externalApis: {
        ...settings.externalApis,
        [key]: value,
      },
    });
  };

  if (loading) {
    return (
      <div>
        <div className="card">
          <p className="loading">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div>
        <div className="card error-message">
          <p>Failed to load settings</p>
          <button className="btn btn-primary" onClick={loadSettings}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>System Settings</h2>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Cache Expiry Settings</h2>
        <p style={{ color: '#718096', marginBottom: '1rem' }}>
          Configure how long different types of data are cached before being refreshed.
        </p>
        
        <div className="settings-section">
          <div className="settings-group">
            <label>Location Data Expiry (days)</label>
            <input
              className="input"
              type="number"
              value={settings.cache.locationExpiryDays}
              onChange={(e) => updateCacheSetting('locationExpiryDays', parseInt(e.target.value))}
              min="1"
              max="365"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>Geolocation data changes rarely</span>
          </div>

          <div className="settings-group">
            <label>Security Data Expiry (days)</label>
            <input
              className="input"
              type="number"
              value={settings.cache.securityExpiryDays}
              onChange={(e) => updateCacheSetting('securityExpiryDays', parseInt(e.target.value))}
              min="1"
              max="90"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>VPN/Proxy detection data</span>
          </div>

          <div className="settings-group">
            <label>Network Data Expiry (days)</label>
            <input
              className="input"
              type="number"
              value={settings.cache.networkExpiryDays}
              onChange={(e) => updateCacheSetting('networkExpiryDays', parseInt(e.target.value))}
              min="1"
              max="365"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>ISP and ASN information</span>
          </div>

          <div className="settings-group">
            <label>Reputation Data Expiry (days)</label>
            <input
              className="input"
              type="number"
              value={settings.cache.reputationExpiryDays}
              onChange={(e) => updateCacheSetting('reputationExpiryDays', parseInt(e.target.value))}
              min="1"
              max="30"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>Threat and abuse data (changes frequently)</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>TOR Project Settings</h2>
        <p style={{ color: '#718096', marginBottom: '1rem' }}>
          Configure TOR exit node detection and update frequency.
        </p>

        <div className="settings-section">
          <div className="settings-group">
            <label>Update Interval (hours)</label>
            <input
              className="input"
              type="number"
              value={settings.torProject.updateIntervalHours}
              onChange={(e) => updateTorSetting('updateIntervalHours', parseInt(e.target.value))}
              min="1"
              max="168"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>How often to fetch the TOR exit node list</span>
          </div>

          <div className="settings-group">
            <label>Bulk List URL</label>
            <input
              className="input"
              type="text"
              value={settings.torProject.bulkListUrl}
              onChange={(e) => updateTorSetting('bulkListUrl', e.target.value)}
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>TOR Project bulk exit list endpoint</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>External API Settings</h2>
        <p style={{ color: '#718096', marginBottom: '1rem' }}>
          Configure external API rate limits and timeouts.
        </p>

        <div className="settings-section">
          <div className="settings-group">
            <label>IP-API Rate Limit (requests/min)</label>
            <input
              className="input"
              type="number"
              value={settings.externalApis.ipApiRateLimit}
              onChange={(e) => updateApiSetting('ipApiRateLimit', parseInt(e.target.value))}
              min="1"
              max="150"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>Free tier: 45 req/min</span>
          </div>

          <div className="settings-group">
            <label>API Timeout (milliseconds)</label>
            <input
              className="input"
              type="number"
              value={settings.externalApis.ipApiTimeout}
              onChange={(e) => updateApiSetting('ipApiTimeout', parseInt(e.target.value))}
              min="1000"
              max="30000"
            />
            <span style={{ fontSize: '0.875rem', color: '#718096' }}>How long to wait for API responses</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button 
            onClick={loadSettings} 
            disabled={saving}
            className="btn btn-secondary"
          >
            Reset to Saved
          </button>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;